import React, { useState, useEffect, useCallback } from "react";
import { ACTIVE_API_BASE_URL } from "../config/apiConfig";
import { ChevronLeft, ChevronRight, Save } from "lucide-react";
import apiClient from "../api/axiosClient";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/FormProduction.css";
import SidebarGuideline from "./SidebarGuideline";
import TextField from "@mui/material/TextField";

import {downloadFile} from '../common'
import useRegistrationPrefill from "../hooks/useRegistrationPrefill";
// Constants for max lengths
const FIELD_MAXLength = 100;
const FIELD_MAX_LENGTH = FIELD_MAXLength; // preserve original name used elsewhere
const COMPANY_PROFILE_MAX_LENGTH = 300;
const COMMENT_MAX_LENGTH = 200;
const TEXTAREA_MAX_LENGTH = 300;
const PHONE_MAX_LENGTH = 10;

// Validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\d{10}$/;

const initialTechnology = {
  technology_name: "",
  technology_provider: "",
  cost: "",
  areas_of_impact: "",
  remarks: "",
};

const explorationFields = [
  { num: "1", label: "2P Oil reserves accretion (MMT)", key: "oil_reserves" },
  { num: "2", label: "2P Gas reserves accretion (BCM)", key: "gas_reserves" },
  { num: "3", label: "Total Reserves Accreted (MTOE)", key: "total_reserves" },
  { num: "4", label: "Finding Cost (INR Million)", key: "finding_cost" },
  {
    num: "5",
    label: "Total Number of exploratory wells drilled",
    key: "exploratory_wells",
  },
  {
    num: "6",
    label: "Number of Hydrocarbon Bearing wells",
    key: "hydrocarbon_wells",
  },
  // { num: "7", label: "Total Seismic Activity", key: "seismic_activity" },
  {
    num: "7",
    label: "Total Seismic Activity - 2D LKM",
    key: "seismic_activity_2d",
  },
  {
    num: "8",
    label: "Total Seismic Activity - 3D SKM",
    key: "seismic_activity_3d",
  },
  {
    num: "9",
    label: "Total Energy Consumed (GJ) in Exploration",
    key: "energy_exploration",
  },
  {
    num: "9.1",
    label: "Total Energy Consumed by the Company (GJ)",
    key: "energy_company",
  },
  {
    num: "9.2",
    label: "Total Capex of the Company (INR Crore)",
    key: "capex_company",
  },
  {
    num: "9.3",
    label: "Total Opex of the Company (INR Crore)",
    key: "opex_company",
  },
  {
    num: "9.4",
    label: "Capex for Exploration (INR Crore)",
    key: "capex_exploration",
  },
  {
    num: "9.5",
    label: "Opex for Exploration (INR Crore)",
    key: "opex_exploration",
  },
  {
    num: "10",
    label: "Number of Exploratory Blocks acquired Standalone",
    key: "blocks_standalone",
  },
  {
    num: "11",
    label: "Number of Exploratory Blocks acquired through Partnership",
    key: "blocks_partnership",
  },
];

const toCamel = (snake) => {
  if (!snake) return snake;
  const parts = snake.split("_");
  const camel = parts
    .map((p, i) =>
      i === 0 ? p.toLowerCase() : p.charAt(0).toUpperCase() + p.slice(1)
    )
    .join("");
  return camel.replace(/(\d)([a-z])/g, (_, d, c) => d + c.toUpperCase());
};

const RegistrationExploration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeItem, setActiveItem] = useState(null);
  const awardTitle =
    location.state?.awardTitle || "Oil & Gas Exploration Company of the Year";

  const [userInfo, setUserInfo] = useState();
  const [formId, setFormId] = useState();
  const [PkId, setPkId] = useState(0);

  // list of the five sub-keys for 8.1..8.5
  const subKeys = [
    "energy_company",
    "capex_company",
    "opex_company",
    "capex_exploration",
    "opex_exploration",
  ];

  // Form state initialization
  const [formData, setFormData] = useState({
    organisationname: "",
    category: "Exploration Company of the Year",
    mailing_address: "",
    authority_name: "",
    authority_title: "",
    authority_phone: "",
    authorityLandline: "",
    authority_email: "",
    applicant_name: "",
    applicant_phone: "",
    applicant_email: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    company_profile: "",
    approving_authority_file: null,
    // approving_authority_file: null,
    declaration: false,
    comment: "",
    ...explorationFields.reduce(
      (acc, { key }) => ({
        ...acc,
        [key]: ["", ""], // [2024, 2023]
      }),
      {}
    ),
    technologies: Array.from({ length: 5 }, () => ({ ...initialTechnology })),
    attachments1: { description: "", file: null },
    attachments2: { description: "", file: null },
    attachments3: { description: "", file: null },
    attachments4: { description: "", file: null },
  });

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("user_info");
      if (stored) {
        const user = JSON.parse(stored);
        setUserInfo(JSON.parse(stored));
        setFormId(`ExplorationRegistration_${user.id}`);

        setFormData((prev) => ({
          ...prev,
          userid: user.id || "",
          firstname: user.first_name || "",
          lastname: user.last_name || "",
          company_name: user.organisation_name || "",
          organisationname: user.organisation_name || "", // prefill organisation_name field
          // Fill applicant_* only (we DON'T prefill contact_* here)
          applicant_name: user.applicant_name || user.first_name || "",
          applicant_phone: user.applicant_phone || "",
          applicant_email: user.email || "",
        }));
      }
    } catch (err) {
      console.warn("Failed to parse user_info from localStorage:", err);
    }
  }, []);

  // This use Effect Prefill user Data using api;
  useEffect(() => {
    if (userInfo && formId) {
      prefillFormData();
    } else {
      console.warn(
        `userInfo OR formId is invalid; Kindly check the method. and data`
      );
    }
  }, [userInfo, formId]);

  const isSubmited = () => formData.form_mode === "submit";

  function prefillFormData() {
    const url = `/registrations/?userid=${userInfo.id}&form_id=${formId}`;
    apiClient
      .get(url)
      .then((res) => {
        if (!Array.isArray(res.data) || res.data.length !== 1) {
          console.log("No prefill data found:", res.data);
          return;
        }

        const _ResData = res.data[0];
        setPkId(_ResData.id || 0);

        // small helper: convert snake_or_kebab to camelCase (used by your handleSubmit)
        const toCamelLocal = (s = "") =>
          String(s)
            .replace(/[-_ ]+([a-zA-Z0-9])/g, (_, c) =>
              c ? c.toUpperCase() : ""
            )
            .replace(/^[A-Z]/, (c) => c.toLowerCase());

        // helper that makes camelCase but ensures 2D/3D uppercase
        const toCamelWith2D3D = (s = "") => {
          const camel = toCamelLocal(s);
          return camel.replace(/2d$/i, "2D").replace(/3d$/i, "3D");
        };

        const newFields = {
          // Basic fields (state uses these names)
          organisationname:
            _ResData.organisation_name ??
            _ResData.organisationName ??
            _ResData.company_name ??
            "",
          category: _ResData.category ?? "Exploration Company of the Year",
          mailing_address:
            _ResData.mailing_address ?? _ResData.mailingAddress ?? "",
          authority_name: _ResData.authority_name ?? "",
          authority_title: _ResData.authority_title ?? "",
          authority_phone: _ResData.authority_phone ?? "",
          authorityLandline:
            _ResData.authority_landline ?? _ResData.authorityLandline ?? "",
          authority_email: _ResData.authority_email ?? "",
          applicant_name:
            _ResData.applicant_name ?? _ResData.contact_name ?? "",
          applicant_phone:
            _ResData.applicant_phone ?? _ResData.contact_phone ?? "",
          applicant_email:
            _ResData.applicant_email ?? _ResData.contact_email ?? "",
          contact_name: _ResData.contact_name ?? "",
          contact_phone: _ResData.contact_phone ?? "",
          contact_email: _ResData.contact_email ?? "",
          company_profile: _ResData.company_profile ?? "",
          comment: _ResData.comment ?? "",
          declaration:
            _ResData.declaration === true || _ResData.declaration === "true",
          form_mode: _ResData.form_mode ?? "draft",
        };

        // --- Exploration numeric pair fields (explorationFields is used to generate keys) ---
        if (Array.isArray(explorationFields)) {
          explorationFields.forEach((entry) => {
            const key = entry.key; // key used in your initial state

            const camel = toCamelLocal(key); // seismicActivity2d
            const camelPretty = toCamelWith2D3D(key); // seismicActivity2D

            const candidates2024 = [
              `${key}_2024`,
              `${key}2024`,
              `${camel}2024`,
              `${camelPretty}2024`,
              `${camelPretty}_2024`,
            ];
            const candidates2023 = [
              `${key}_2023`,
              `${key}2023`,
              `${camel}2023`,
              `${camelPretty}2023`,
              `${camelPretty}_2023`,
            ];

            let v24 = "";
            let v23 = "";
            for (const c of candidates2024) {
              if (
                _ResData.hasOwnProperty(c) &&
                _ResData[c] !== null &&
                _ResData[c] !== undefined
              ) {
                v24 = _ResData[c];
                break;
              }
            }
            for (const c of candidates2023) {
              if (
                _ResData.hasOwnProperty(c) &&
                _ResData[c] !== null &&
                _ResData[c] !== undefined
              ) {
                v23 = _ResData[c];
                break;
              }
            }

            // Last-resort case-insensitive lookup for camelPretty + year
            if (v24 === "" || v24 === null || v23 === "" || v23 === null) {
              const lowerResKeys = Object.keys(_ResData).reduce((acc, k) => {
                acc[k.toLowerCase()] = k;
                return acc;
              }, {});
              const tryKey24 = `${camelPretty}2024`.toLowerCase();
              const tryKey23 = `${camelPretty}2023`.toLowerCase();
              if ((v24 === "" || v24 === null) && lowerResKeys[tryKey24]) {
                v24 = _ResData[lowerResKeys[tryKey24]];
              }
              if ((v23 === "" || v23 === null) && lowerResKeys[tryKey23]) {
                v23 = _ResData[lowerResKeys[tryKey23]];
              }
            }

            // Keep controlled inputs as strings
            newFields[key] = [
              v24 === null || v24 === undefined ? "" : String(v24),
              v23 === null || v23 === undefined ? "" : String(v23),
            ];

            // Also set fallback flat names (camel & pretty forms)
            newFields[`${camel}2024`] =
              v24 === null || v24 === undefined ? "" : String(v24);
            newFields[`${camel}2023`] =
              v23 === null || v23 === undefined ? "" : String(v23);
            newFields[`${camelPretty}2024`] =
              v24 === null || v24 === undefined ? "" : String(v24);
            newFields[`${camelPretty}2023`] =
              v23 === null || v23 === undefined ? "" : String(v23);
          });
        } else {
          console.warn(
            "explorationFields is not an array — skipping numeric pairs prefill."
          );
        }

        // --- Technologies: prefer flat fields technology_1_* .. technology_5_* if present,
        // otherwise fall back to _ResData.technologies (JSON/array/object) ---
        (function buildTechnologies() {
          const makeTech = (src = {}) => ({
            technology_name:
              src.technology_name ??
              src.tech ??
              src.technology ??
              src.name ??
              "",
            technology_provider:
              src.technology_provider ??
              src.provider ??
              src.project ??
              src.client ??
              "",
            cost:
              src.technology_cost != null
                ? String(src.technology_cost)
                : src.cost != null
                ? String(src.cost)
                : src.value != null
                ? String(src.value)
                : "",
            areas_of_impact:
              src.areas_of_impact ??
              src.areas ??
              src.impact ??
              src.areasOfImpact ??
              "",
            remarks: src.remarks ?? src.note ?? src.comments ?? "",
          });

          // Detect if flat fields exist
          let flatFound = false;
          for (let i = 1; i <= 5; i++) {
            if (
              _ResData.hasOwnProperty(`technology_${i}_name`) ||
              _ResData.hasOwnProperty(`technology_${i}_provider`) ||
              _ResData.hasOwnProperty(`technology_${i}_cost`) ||
              _ResData.hasOwnProperty(`technology_${i}_areas_of_impact`) ||
              _ResData.hasOwnProperty(`technology_${i}_remarks`)
            ) {
              flatFound = true;
              break;
            }
          }

          let techArr = [];

          if (flatFound) {
            techArr = Array.from({ length: 5 }, (_, i) => {
              const idx = i + 1;
              return {
                technology_name: _ResData[`technology_${idx}_name`] ?? "",
                technology_provider:
                  _ResData[`technology_${idx}_provider`] ?? "",
                cost:
                  _ResData[`technology_${idx}_cost`] != null
                    ? String(_ResData[`technology_${idx}_cost`])
                    : "",
                areas_of_impact:
                  _ResData[`technology_${idx}_areas_of_impact`] ?? "",
                remarks: _ResData[`technology_${idx}_remarks`] ?? "",
              };
            });
          } else {
            // Fallback: try to read _ResData.technologies (string or array or object)
            let rawTechs = [];
            if (Array.isArray(_ResData.technologies)) {
              rawTechs = _ResData.technologies;
            } else if (typeof _ResData.technologies === "string") {
              try {
                rawTechs = JSON.parse(_ResData.technologies) || [];
              } catch (e) {
                rawTechs = [];
              }
            } else if (
              _ResData.technologies &&
              typeof _ResData.technologies === "object"
            ) {
              rawTechs = Array.isArray(_ResData.technologies)
                ? _ResData.technologies
                : [_ResData.technologies];
            }

            techArr = Array.from({ length: 5 }, (_, i) => {
              const src = rawTechs[i] || {};
              return makeTech(src);
            });
          }

          // ensure independent objects
          newFields.technologies = techArr.map((t) => ({ ...t }));
        })();

        // --- Attachments (1..4) keep file:null and url for preview ---
        for (let n = 1; n <= 4; n++) {
          const descCandidates = [
            `attachments${n}_description`,
            `attachments${n}_desc`,
            `attachments${n}Description`,
          ];
          const fileCandidates = [
            `attachments${n}`,
            `attachments${n}_url`,
            `attachments${n}Url`,
          ];

          let desc = "";
          for (const d of descCandidates) {
            if (
              _ResData.hasOwnProperty(d) &&
              _ResData[d] !== undefined &&
              _ResData[d] !== null
            ) {
              desc = _ResData[d];
              break;
            }
          }

          let urlVal = null;
          for (const f of fileCandidates) {
            if (
              _ResData.hasOwnProperty(f) &&
              _ResData[f] !== undefined &&
              _ResData[f] !== null
            ) {
              urlVal = _ResData[f];
              break;
            }
          }

          newFields[`attachments${n}`] = {
            description: desc ?? "",
            file: null,
            url: urlVal ?? null,
          };
        }

        // --- Signed form file: keep url for preview, file null until user uploads ---
  newFields.approvingAuthoritySignature = null;
      newFields.approvingAuthoritySignatureUrl =
        _ResData.approving_authority_file ??
        _ResData.approving_authority_file_url ??
        null;

        // Also add some common aliases in case other parts of your code read different names
        if (_ResData.organisation_name && !newFields.organisationname) {
          newFields.organisationname = _ResData.organisation_name;
        }
        if (_ResData.mailing_address && !newFields.mailing_address) {
          newFields.mailing_address = _ResData.mailing_address;
        }

        // Apply state immutably so React updates all controlled inputs reliably
        setFormData((prev) => ({ ...prev, ...newFields }));

        // optional debug: inspect prefilled technologies
        console.log("Prefilled technologies:", newFields.technologies);
        console.log("Registrations prefill applied, pkId:", _ResData.id);
      })
      .catch((err) => {
        console.error("prefillFormData error:", err);
      });
  }

  const [currentStep, setCurrentStep] = useState(1);
  const [copyApplicantData, setCopyApplicantData] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Validation helpers
  const validateEmail = (email) => emailRegex.test(email);
  const validatePhone = (phone) => phoneRegex.test(phone);

  // Clear error on field
  const clearFieldError = useCallback((field) => {
    setFieldErrors((prev) => {
      if (prev[field]) {
        const { [field]: omitted, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  // Helper: detect whether energy_exploration has any non-empty value
  const isEnergyExplorationFilled = () =>
    Array.isArray(formData.energy_exploration) &&
    formData.energy_exploration.some(
      (v) => v !== "" && v !== null && v !== undefined
    );

  // Helper: detect whether any of the 5 subfields has any non-empty value
  const isAnySubfieldFilled = () =>
    subKeys.some(
      (k) =>
        Array.isArray(formData[k]) &&
        formData[k].some((v) => v !== "" && v !== null && v !== undefined)
    );
   const viewFile =  async (serverPath) => {
    // "https://award-backend.fipi.org.in/media/
    const extractPath = serverPath.split('/').filter(
  (val) =>
    val !== "" && // remove empty strings
    val !== "http:" &&
    val !== "https:" &&
    val !== "media" &&
    !val.startsWith("award") &&
    !val.startsWith("localhost")
).join('/');
    
    downloadFile(extractPath)
  }
  // Handle changes for inputs/textareas/selects
  const handleChange = (name, value, index = null) => {
    if ([1, 2, 4].includes(currentStep)) {
      let applicableMaxLength = FIELD_MAX_LENGTH;
      if (["company_profile"].includes(name)) applicableMaxLength = 200;
      else if (name === "comment" || name.includes("_remarks"))
        applicableMaxLength = COMMENT_MAX_LENGTH;

      if (typeof value === "string") {
        if (name === "company_profile") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 300) {
            alert("Maximum 300 words allowed.");
            return;
          }
        } else if (name === "comment") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 200) {
            alert("Maximum 200 words allowed in Comments.");
            return;
          }
        }
      }
    }

    // Name validation
    if (
      ["organisation_name", "authority_name", "contact_name"].includes(name)
    ) {
      const isValid = /^[A-Za-z\s]*$/.test(value);
      if (!isValid && value !== "") {
        alert("Only letters and spaces are allowed.");
        return;
      }
    }

    // Phone validation
    if (name === "authority_phone" || name === "contact_phone") {
      const numericValue = value.replace(/\D/g, "").slice(0, PHONE_MAX_LENGTH);
      if (numericValue.length > PHONE_MAX_LENGTH) {
        alert("Phone number must not exceed 10 digits.");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
      clearFieldError(name);
      return;
    }

    // Quantitative fields validation and mutual hide/clear logic
    if (index !== null) {
      // if (value === "") {
      //   clearFieldError(name);
      // } else {
      //   const numVal = Number(value);
      //   if (numVal < 0) {
      //     alert("Value cannot be negative.");
      //     return;
      //   } else {
      //     clearFieldError(name);
      //   }
      // }

      setFormData((prev) => {
        const updatedArray = [...(prev[name] || ["", ""])];
        updatedArray[index] = value;

        const newState = {
          ...prev,
          [name]: updatedArray,
        };

        // If user enters into energy_exploration, clear the 5 subfields
        if (name === "energy_exploration" && value !== "") {
          subKeys.forEach((k) => {
            newState[k] = ["", ""];
          });
        }

        // If user enters into any of the five subfields, clear energy_exploration
        if (subKeys.includes(name) && value !== "") {
          newState["energy_exploration"] = ["", ""];
        }

        return newState;
      });

      return;
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: typeof value === "boolean" ? value : value,
      }));
    }

    // Clear step-specific errors
    if (name === "organisation_name" && value && currentStep === 1)
      setError("");
    if (name === "mailing_address" && value.trim() && currentStep === 1)
      setError("");
    if (name === "authority_name" && value && currentStep === 2) setError("");
    if (name === "authority_title" && value && currentStep === 2) setError("");
    if (name === "authority_email" && value && currentStep === 2) setError("");
    if (name === "authority_phone" && value && currentStep === 2) setError("");
  };

  // Handle technology fields
  const handleTechChange = (index, field, value) => {
    let applicableMaxLength = FIELD_MAX_LENGTH;
    if (field === "remarks") applicableMaxLength = TEXTAREA_MAX_LENGTH;

    if (typeof value === "string" && value.length > applicableMaxLength) {
      alert(`Your value must not exceed ${applicableMaxLength} characters.`);
      return;
    }

    if (field === "cost" && value !== "" && Number(value) < 0) {
      alert("Cost cannot be negative.");
      return;
    }

    setFormData((prev) => {
      const techs = Array.isArray(prev.technologies)
        ? prev.technologies.map((t, i) =>
            i === index ? { ...t, [field]: value } : t
          )
        : Array.from({ length: 5 }, (_, i) =>
            i === index
              ? { ...initialTechnology, [field]: value }
              : { ...initialTechnology, ...(prev.technologies?.[i] || {}) }
          );

      return { ...prev, technologies: techs };
    });
    clearFieldError(`technologies[${index}].${field}`);
  };

  // Handle checkbox for copying applicant data
  // const handleCopyApplicantToggle = (e) => {
  //   const checked = e.target.checked;
  //   setCopyApplicantData(checked);
  //   if (checked) {
  //     setFormData((prev) => ({
  //       ...prev,
  //       contact_name: prev.applicant_name,
  //       contact_email: prev.email,
  //       contact_phone: prev.applicant_phone,
  //     }));
  //     clearFieldError("contact_name");
  //     clearFieldError("contact_email");
  //     clearFieldError("contact_phone");
  //   } else {
  //     setFormData((prev) => ({
  //       ...prev,
  //       contact_name: "",
  //       contact_email: "",
  //       contact_phone: "",
  //     }));
  //   }
  // };

  // If the checkbox is ON and applicant fields change, keep contact fields synced.
  useEffect(() => {
    if (!copyApplicantData) return;

    let user = {};
    try {
      const raw = localStorage.getItem("user_info");
      if (raw) user = JSON.parse(raw);
    } catch (err) {
      console.warn(
        "Failed to parse user_info from localStorage in sync effect:",
        err
      );
    }

    setFormData((prev) => ({
      ...prev,
      contact_name: user.applicant_name || prev.applicant_name || "",
      contact_phone: user.applicant_phone || prev.applicant_phone || "",
      contact_email: user.email || prev.applicant_email || "",
    }));

    clearFieldError("contact_name");
    clearFieldError("contact_phone");
    clearFieldError("contact_email");
    // We intentionally do not add formData.* to the dependency array here that would cause infinite loops.
    // The dependency below ensures we re-run when copyApplicantData toggles.
  }, [copyApplicantData, clearFieldError]);

  const handleCopyApplicantToggle = (e) => {
    const checked = e.target.checked;
    // setCopyApplicantData(checked);

    const model = {
      contact_name: "",
      contact_phone: "",
      contact_email: "",
    };

    if (checked) {
      const userInfo = JSON.parse(sessionStorage.getItem("user_info"));
      if (userInfo) {
        model.contact_name = userInfo.applicant_name;
        model.contact_phone = userInfo.applicant_phone;
        model.contact_email = userInfo.email;
      }
    }

    setFormData((prev) => ({ ...prev, ...model }));

    clearFieldError("contact_name");
    clearFieldError("contact_phone");
    clearFieldError("contact_email");
  };

  // Handle attachment changes
  const handleAttachmentChange = (key, field, value, event = null) => {
    if (field === "file" && value) {
      const file = value;
      const maxSizeInBytes = 5 * 1024 * 1024;
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];

      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, PNG, and PDF files are allowed for attachments.");
        if (event) event.target.value = null;
        return;
      }
      if (file.size > maxSizeInBytes) {
        alert("File size must not exceed 5 MB for attachments.");
        if (event) event.target.value = null;
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
    clearFieldError(key);
  };

  // Handle approving authority file upload
  const handleApprovingAuthorityChange = (files) => {
    const file = files[0];
    if (file) {
      const maxSizeInBytes = 5 * 1024 * 1024;
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];

      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, PNG, and PDF files are allowed for the signature.");
        return;
      }
      if (file.size > maxSizeInBytes) {
        alert("File size must not exceed 5 MB for the signature.");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        approving_authority_file: file,
      }));
      clearFieldError("approving_authority_file");
    }
  };

  // Print form handler
  const handleFile = (e, fieldName) => {
    const file = e.target.files[0];
    if (file && file.size > 5 * 1024 * 1024) {
      setFormData((f) => ({ ...f, fileError: "Max file size 5 MB" }));
    } else {
      setFormData((f) => ({ ...f, [fieldName]: file, fileError: "" }));
    }
  };

  // small helper to escape user-provided strings so HTML doesn't break
  function escapeHtml(str = "") {
    return String(str)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  const handlePrint = () => {
    const title = escapeHtml(awardTitle || "");

    // build bodyHtml but keep all your original data/structure
    const bodyHtml = `
    <div style="font-family: Arial, sans-serif; padding: 20px;">
      <h1 style="text-align: center; color: #1e40af;">${title}</h1>
      <h2>Organization & Contact Details:</h2>
      <p><strong>Organisation Name:</strong> ${escapeHtml(
        formData.organisationname || ""
      )}</p>
      <p><strong>Postal Address:</strong> ${escapeHtml(
        formData.mailing_address || ""
      )}</p>
      <p><strong>Approving Authority Name:</strong> ${escapeHtml(
        formData.authority_name || ""
      )}</p>
      <p><strong>Approving Authority Designation:</strong> ${escapeHtml(
        formData.authority_title || ""
      )}</p>
      <p><strong>Approving Authority Landline:</strong> ${escapeHtml(
        formData.authorityLandline || ""
      )}</p>

      <p><strong>Approving Authority Phone:</strong> ${escapeHtml(
        formData.authority_phone || ""
      )}</p>
      <p><strong>Approving Authority Email:</strong> ${escapeHtml(
        formData.authority_email || ""
      )}</p>

      <h2>Nodal Official Contact Details:</h2>
      <p><strong>Contact Name:</strong> ${escapeHtml(
        formData.contact_name || ""
      )}</p>
      <p><strong>Contact Phone:</strong> ${escapeHtml(
        formData.contact_phone || ""
      )}</p>
      <p><strong>Contact Email:</strong> ${escapeHtml(
        formData.contact_email || ""
      )}</p>
      <p><strong>Brief write up on company profile and the activities with
                specific reference to Exploration operations during the year
                2024-25:</strong> ${escapeHtml(
                  formData.company_profile || ""
                )}</p>

      <h2>Quantitative Information - Part 1</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
        <thead>
          <tr>
            <th style="border: 1px solid #000; padding: 2px;">S.No</th>
            <th style="border: 1px solid #000; padding: 16px;">Particulars</th>
            <th style="border: 1px solid #000; padding: 2px;">2024-25</th>
            <th style="border: 1px solid #000; padding: 2px;">2023-24</th>
          </tr>
        </thead>
        <tbody>
          ${
            Array.isArray(explorationFields)
              ? explorationFields
                  .slice(0, 5)
                  .map(({ num, label, key }) => {
                    const val0 =
                      formData[key] && formData[key][0]
                        ? escapeHtml(formData[key][0])
                        : "";
                    const val1 =
                      formData[key] && formData[key][1]
                        ? escapeHtml(formData[key][1])
                        : "";
                    return `
                    <tr>
                      <td style="border: 1px solid #000; padding: 4px; text-align: center; vertical-align: middle;">${escapeHtml(
                        num
                      )}</td>
                      <td style="border: 1px solid #000; padding: 16px; text-align: start; vertical-align: middle;">${escapeHtml(
                        label
                      )}</td>
                      <td style="border: 1px solid #000; padding: 4px; text-align: center; vertical-align: middle;">${val0}</td>
                      <td style="border: 1px solid #000; padding: 4px; text-align: center; vertical-align: middle;">${val1}</td>
                    </tr>
                  `;
                  })
                  .join("")
              : ""
          }
        </tbody>
      </table>

      <h2>Quantitative Information - Part 2</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
        <thead>
          <tr>
            <th style="border: 1px solid #000; padding: 8px;">S.No</th>
            <th style="border: 1px solid #000; padding: 7px;">Particulars</th>
            <th style="border: 1px solid #000; padding: 13px;">2024-25</th>
            <th style="border: 1px solid #000; padding: 12px;">2023-24</th>
          </tr>
        </thead>
        <tbody>
          ${
            Array.isArray(explorationFields)
              ? explorationFields
                  .slice(5)
                  .map(({ num, label, key }) => {
                    const val0 =
                      formData[key] && formData[key][0]
                        ? escapeHtml(formData[key][0])
                        : "";
                    const val1 =
                      formData[key] && formData[key][1]
                        ? escapeHtml(formData[key][1])
                        : "";
                    return `
                    <tr>
                      <td style="border: 1px solid #000; padding: 8px; text-align: center; vertical-align: middle;">${escapeHtml(
                        num
                      )}</td>
                      <td style="border: 1px solid #000; padding: 8px; text-align: start; vertical-align: middle;">${escapeHtml(
                        label
                      )}</td>
                      <td style="border: 1px solid #000; padding: 8px; text-align: center; vertical-align: middle;">${val0}</td>
                      <td style="border: 1px solid #000; padding: 8px; text-align: center; vertical-align: middle;">${val1}</td>
                    </tr>
                  `;
                  })
                  .join("")
              : ""
          }
        </tbody>
      </table>

      <h2>Technologies</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
        <thead>
          <tr>
            <th style="border: 1px solid #000; padding: 8px;">S.No</th>
            <th style="border: 1px solid #000; padding: 8px;">Technology Name</th>
            <th style="border: 1px solid #000; padding: 8px;">Provider</th>
            <th style="border: 1px solid #000; padding: 8px;">Cost (INR Crore)</th>
            <th style="border: 1px solid #000; padding: 8px;">Areas of Impact</th>
            <th style="border: 1px solid #000; padding: 8px;">Remarks</th>
          </tr>
        </thead>
        <tbody>
          ${
            Array.isArray(formData.technologies)
              ? formData.technologies
                  .map(
                    (t, i) => `
                  <tr>
                    <td style="border: 1px solid #000; padding: 8px; text-align: center; vertical-align: middle;">${
                      i + 1
                    }</td>
                    <td style="border: 1px solid #000; padding: 8px;">${escapeHtml(
                      t.technology_name || ""
                    )}</td>
                    <td style="border: 1px solid #000; padding: 8px;">${escapeHtml(
                      t.technology_provider || ""
                    )}</td>
                    <td style="border: 1px solid #000; padding: 8px;">${escapeHtml(
                      t.cost || ""
                    )}</td>
                    <td style="border: 1px solid #000; padding: 8px;">${escapeHtml(
                      t.areas_of_impact || ""
                    )}</td>
                    <td style="border: 1px solid #000; padding: 8px;">${escapeHtml(
                      t.remarks || ""
                    )}</td>
                  </tr>
                `
                  )
                  .join("")
              : ""
          }
        </tbody>
      </table>

      <h2>Attachments</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
        <thead>
          <tr>
            <th style="border: 1px solid #000; padding: 8px;">S.No</th>
            <th style="border: 1px solid #000; padding: 8px;">Description</th>
            <th style="border: 1px solid #000; padding: 8px;">File Name</th>
          </tr>
        </thead>
        <tbody>
          ${[1, 2, 3, 4]
            .map((i) => {
              const key = `attachments${i}`;
              const att = formData[key] || {};
              const desc = escapeHtml(att.description || "");
              const fname = escapeHtml((att.file && att.file.name) || "");
              return `
                <tr>
                  <td style="border: 1px solid #000; padding: 8px; text-align: center; vertical-align: middle;">${i}</td>
                  <td style="border: 1px solid #000; padding: 8px;">${desc}</td>
                  <td style="border: 1px solid #000; padding: 8px;">${fname}</td>
                </tr>
              `;
            })
            .join("")}
        </tbody>
      </table>

      <h2>Comments</h2>
      <p>${escapeHtml(formData.comment || "")}</p>

      <br/>
      <h2>Declaration</h2>
      <p>I declare that the information submitted is true and complete.</p>
      <br/>
      <p><strong>Approving Authority Name:</strong> ${escapeHtml(
        formData.authority_name || ""
      )}</p>
      <p><strong>Approving Authority Designation:</strong> ${escapeHtml(
        formData.authority_title || ""
      )}</p>
      <p><strong>Signed on:</strong> ${escapeHtml(
        formData.signatureDate || new Date().toLocaleDateString()
      )}</p>
      <p><strong> Approving Authority Signature: </strong></p>
    </div>
  `;

    // full HTML with title to prevent about:blank showing in header
    const fullHtml = `<!doctype html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>${title}</title>
        <style>
          @page { margin: 20mm; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
        </style>
      </head>
      <body>${bodyHtml}</body>
    </html>`;

    const printWindow = window.open("", "_blank", "height=600,width=800");
    printWindow.document.open();
    printWindow.document.write(fullHtml);
    printWindow.document.close();

    // ensure title is set for the print header
    try {
      printWindow.document.title = title;
    } catch (e) {
      // ignore if cross-origin or other oddity; title is already present in head
      console.warn("Could not set print window title:", e);
    }

    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      // optionally close after print:
      // printWindow.close();
    }, 300);
  };

  const hasEmptyFieldsStep3 = () => {
    for (const { key } of explorationFields.slice(0, 5)) {
      const values = formData[key];
      if (
        !values ||
        values.some((v) => v === "" || v === null || v === undefined)
      ) {
        return true;
      }
    }
    return false;
  };

  // const hasEmptyFieldsStep4 = () => {
  //   // Determine visible keys in step 4 (skip hidden ones)
  //   const visibleKeys = explorationFields
  //     .slice(5)
  //     .map((f) => f.key)
  //     .filter((key) => {
  //       if (key === "energy_exploration") {
  //         return !isAnySubfieldFilled();
  //       }
  //       if (subKeys.includes(key)) {
  //         return !isEnergyExplorationFilled();
  //       }
  //       return true;
  //     });

  //   // Check numeric inputs for visible keys
  //   for (const key of visibleKeys) {
  //     const values = formData[key];
  //     if (
  //       !values ||
  //       values.some((v) => v === "" || v === null || v === undefined)
  //     ) {
  //       return true;
  //     }
  //   }

  //   // Check technologies array (if exists)
  //   if (formData.technologies) {
  //     for (const tech of formData.technologies) {
  //       if (
  //         !tech.technology_name?.trim() ||
  //         !tech.technology_provider?.trim() ||
  //         tech.cost === "" ||
  //         tech.cost === null ||
  //         tech.cost === undefined ||
  //         !tech.areas_of_impact?.trim()
  //       ) {
  //         return true;
  //       }
  //     }
  //   }

  //   // Check comment field (optional in your earlier code but kept)
  //   if (!formData.comment || formData.comment.trim() === "") {
  //     return true;
  //   }
  //   return false;
  // };

  // Navigation handlers
 const hasEmptyFieldsStep4 = () => {
    const visibleKeys = explorationFields
      .slice(5)
      .map((f) => f.key)
      .filter((key) => {
        if (key === "energy_exploration") {
          return !isAnySubfieldFilled();
        }
        if (subKeys.includes(key)) {
          return !isEnergyExplorationFilled();
        }
        return true;
      });

    for (const key of visibleKeys) {
      const values = formData[key];
      if (
        !values ||
        values.some((v) => v === "" || v === null || v === undefined)
      ) {
        return true;
      }
    }

    if (formData.technologies) {
      for (const tech of formData.technologies) {
        if (
          !tech.technology_name?.trim() ||
          !tech.technology_provider?.trim() ||
          tech.cost === "" ||
          tech.cost === null ||
          tech.cost === undefined ||
          !tech.areas_of_impact?.trim()
        ) {
          return true;
        }
      }
    }


    
    return false;
  };
  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.mailing_address.trim()) {
        alert("Mailing address is required.");
        return;
      }
    }
    if (currentStep === 2) {
      if (!formData.authority_name) {
        alert("Authority name is required.");
        return;
      }
      if (!formData.authority_title) {
        alert("Authority designation is required.");
        return;
      }
      if (
        !formData.authority_email ||
        !validateEmail(formData.authority_email)
      ) {
        alert("Please enter a valid Authority email.");
        return;
      }
      if (!formData.authorityLandline) {
        alert("Authority Landline is required.");
        return;
      }
      if (!formData.contact_name) {
        alert("Contact name is required.");
        return;
      }
      if (formData.contact_email && !validateEmail(formData.contact_email)) {
        alert("Please enter a valid Contact email.");
        return;
      }
      if (
        formData.contact_phone &&
        !validatePhone(formData.contact_phone) &&
        !copyApplicantData
      ) {
        alert("Contact phone must be exactly 10 digits.");
        return;
      }
      if (!formData.company_profile) {
        alert("Company Profile is required.");
        return;
      }
    }
    if (currentStep === 3 && hasEmptyFieldsStep3()) {
      if (!window.confirm("Data not entered, If you wish to continue?")) {
        return; // Stop advancing
      }
    }

    if (currentStep === 4 && hasEmptyFieldsStep4()) {
      if (!window.confirm("Data not entered, If you wish to continue?")) {
        return;
      }
    }

    // IMPORTANT: removed the confirmation popups for Step 3 and Step 4
    // (the behaviour requested by the user)

    setError("");
    if (currentStep < 5) setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  // Save draft handler

  const handleSaveDraft = () => {
    localStorage.setItem(
      "registrationExplorationDraft",
      JSON.stringify({ formData })
    );
    handleSubmit(null);
    alert("Draft Saved!");
  };

  const toCamel = (s = "") =>
    String(s)
      .replace(/[-_ ]+([a-zA-Z0-9])/g, (_, c) => (c ? c.toUpperCase() : ""))
      .replace(/^[A-Z]/, (m) => m.toLowerCase());

  const safeParseJSON = (raw) => {
    try {
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.warn("safeParseJSON failed:", err);
      return null;
    }
  };

  const MAX_FILE_SIZE = 40 * 1024 * 1024; // 40 MB (adjust if needed)

  //Final Submit button

  const finalSubmit = () => {
    handleSubmit(null, "submited");
  };

  async function handleSubmit(event, clickType = "draft") {
    if (event) {
      event?.preventDefault();
    }
    console.log(
      "############################ Save the DAta on Server ##########################"
    );
    // 1) Make a local copy of formData so we don't rely on async state updates
    let payload = { ...formData };
    // FIX: Declare user before using it (was causing 'user is not defined' error)
    let user = {};
    try {
      const raw = localStorage.getItem("user_info");
      if (raw) user = safeParseJSON(raw) || {};
    } catch (err) {
      console.warn("Failed to parse user_info:", err);
    }

    // 2) merge formData + user info for final send/validation
    const data = {
      ...formData,
      firstname: formData.firstname || user.first_name || "",
      lastname: formData.lastname || user.last_name || "",
      userid: formData.userid || user.id || "",
      company_name: formData.company_name || user.organisation_name || "",
      organisationname:
        formData.organisationname || user.organisation_name || "",
      contact_name: formData.contact_name || user.applicant_name || "",
      contact_phone: formData.contact_phone || user.applicant_phone || "",
      contact_email: formData.contact_email || user.email || "",
    };

    // 3) client-side validations

    // 4) Build FormData
    const fd = new FormData();

    // Identity & contact fields (matches your model field names)
    fd.append("organisation_name", data.organisationname || "");
    fd.append("category", data.category || "");
    fd.append("firstname", data.firstname || "");
    fd.append("lastname", data.lastname || "");
    fd.append("userid", data.userid || "");
    fd.append("company_name", data.company_name || "");
    fd.append("mailing_address", data.mailing_address || "");

    // Authority & contact
    fd.append("authority_name", data.authority_name || "");
    fd.append("authority_title", data.authority_title || "");
    fd.append("authority_phone", data.authority_phone || "");
    fd.append("authorityLandline", data.authorityLandline || "");
    fd.append("authority_email", data.authority_email || "");

    fd.append("contact_name", data.contact_name || "");
    fd.append("contact_phone", data.contact_phone || "");
    fd.append("contact_email", data.contact_email || "");

    // Company profile, comment, declaration
    fd.append("company_profile", data.company_profile || "");
    fd.append("comment", data.comment || "");
    fd.append("declaration", data.declaration ? "true" : "false");

    fd.append("form_id", formId);
    fd.append("form_mode", clickType);
    // Add Table Pk Id;
    if (PkId > 0) fd.append("id", PkId);
    if (payload.approving_authority_file instanceof File) {
      fd.append(
        "approving_authority_file",
        payload.approving_authority_file
      );
    }

    // Signed form file (support File or FileList/array)
    // (kept commented as in your previous version; you can re-enable if needed)
    // if (data.approving_authority_file) { ... }

    // 5) Exploration numeric & year-pair fields
    if (Array.isArray(explorationFields)) {
      for (const entry of explorationFields) {
        const key = entry.key;
        const camelKey = toCamel(key);
        const val = data.hasOwnProperty(key) ? data[key] : data[camelKey];

        // --- NEW: handle seismic fields specially if they include 2d/3d in key ---
        // We'll append both snake_case_year and camelCaseYear variants so backend accepts either.
        const isSeismic2D = /2d$/i.test(key) || /_2d$/i.test(key);
        const isSeismic3D = /3d$/i.test(key) || /_3d$/i.test(key);
        let y24 = "";
        let y23 = "";

        if (Array.isArray(val)) {
          [y24 = "", y23 = ""] = val;
        } else if (val && typeof val === "object") {
          y24 = val["2024"] ?? val["y2024"] ?? val["y24"] ?? val["y_24"] ?? "";
          y23 = val["2023"] ?? val["y2023"] ?? val["y23"] ?? val["y_23"] ?? "";
        } else if (typeof val === "string" || typeof val === "number") {
          y24 = val;
          y23 = "";
        }

        // base is camel returned by toCamel(key) (e.g. seismicActivity2d or seismicActivity3d or other keys)
        const baseCamel = camelKey;
        // create a nicer Camel with 2D/3D uppercase if necessary
        const baseCamelPretty = baseCamel
          .replace(/2d$/i, "2D")
          .replace(/3d$/i, "3D");

        // Also keep the original snake-case entry.key (likely already snake) as baseSnake
        const baseSnake = key;

        // Append both naming variants so backend picks one it expects:
        // camel-style: seismicActivity2D2024
        fd.append(`${baseCamelPretty}2024`, y24 ?? "");
        fd.append(`${baseCamelPretty}2023`, y23 ?? "");

        // snake-style: seismic_activity_2d_2024
        fd.append(`${baseSnake}_2024`, y24 ?? "");
        fd.append(`${baseSnake}_2023`, y23 ?? "");

        // If entry.key was a generic 'seismic_activity', still append the generic field
        if (key === "seismic_activity") {
          fd.append("seismic_activity", val ?? "");
        }

        // For non-seismic, your previous behavior (camel-only) — keep for compatibility
        if (!isSeismic2D && !isSeismic3D && key !== "seismic_activity") {
          // previous behavior appended camelKey2024/camelKey2023 (keep it)
          fd.append(`${baseCamel}2024`, y24 ?? "");
          fd.append(`${baseCamel}2023`, y23 ?? "");
        }
      }
    } else {
      console.warn(
        "explorationFields is not an array; skipping exploration fields."
      );
    }

    [1, 2, 3, 4].forEach((n) => {
      const slot = payload[`attachments${n}`] || {};
      if (slot.description) fd.append(`attachments${n}_desc`, slot.description);
      if (slot.file instanceof File) fd.append(`attachments${n}`, slot.file);
    });

    // 7) Nested technologies: send JSON string; serializer handles JSON or array
    try {
      fd.append("technologies", JSON.stringify(data.technologies || []));
    } catch (err) {
      fd.append("technologies", "[]");
    }

    // --- NEW: Also append explicit indexed technology_* fields matching your model ---
    // model fields: technology_name, technology_provider, cost, areas_of_impact, remarks
    // front-end tech objects may have keys like: tech, project, client, cost, value, areas_of_impact, remarks
    try {
      //   const techs = Array.isArray(data.technologies) ? data.technologies : [];
      //   // ensure at least 5 items so backend fields exist (optional)
      //   const count = Math.max(techs.length, 5);
      //   for (let i = 0; i < count; i++) {
      //     const t = techs[i] || {};
      //     // flexible mapping:
      //     const name = t.tech ?? t.technology_name ?? t.name ?? data[`technology${i + 1}Tech`] ?? "";
      //     const provider =
      //       t.project ??
      //       t.provider ??
      //       t.technology_provider ??
      //       data[`technology${i + 1}Project`] ??
      //       data[`technology${i + 1}Client`] ??
      //       "";
      //     const costRaw = t.cost ?? t.technology_cost ?? data[`technology${i + 1}Cost`] ?? "";
      //     const areas = t.areas_of_impact ?? t.areas ?? t.impact ?? "";
      //     const remarks = t.remarks ?? t.note ?? "";

      //     // append using model-friendly names technology_1_name, technology_1_provider, ...
      //     fd.append(`technology_${i + 1}_name`, name ?? "");
      //     fd.append(`technology_${i + 1}_provider`, provider ?? "");
      //     // cost: try to append parsed decimal if possible; else append raw string so backend may reject if invalid
      //     if (costRaw !== null && costRaw !== undefined && String(costRaw).trim() !== "") {
      //       const costParsed = (function parseDecimalLocal(s) {
      //         if (s === "" || s === null || s === undefined) return null;
      //         const n = Number(String(s).replace(/,/g, "").trim());
      //         return Number.isFinite(n) ? n : null;
      //       })(costRaw);
      //       if (costParsed !== null) fd.append(`technology_${i + 1}_cost`, String(costParsed));
      //       else fd.append(`technology_${i + 1}_cost`, String(costRaw));
      //     } else {
      //       fd.append(`technology_${i + 1}_cost`, "");
      //     }
      //     fd.append(`technology_${i + 1}_areas_of_impact`, areas ?? "");
      //     fd.append(`technology_${i + 1}_remarks`, remarks ?? "");

      //     // Also append project/client raw fields (optional; backend may ignore extra fields)
      //     fd.append(`technology_${i + 1}_project`, t.project ?? data[`technology${i + 1}Project`] ?? "");
      //     fd.append(`technology_${i + 1}_client`, t.client ?? data[`technology${i + 1}Client`] ?? "");
      //   }
      // }
      const techs = Array.isArray(data.technologies)
        ? data.technologies
        : Array.from({ length: 5 }, () => ({ ...initialTechnology }));

      for (let i = 0; i < 5; i++) {
        const t = techs[i] || { ...initialTechnology };
        const idx = i + 1;

        fd.append(`technology_${idx}_name`, t.technology_name ?? "");
        fd.append(`technology_${idx}_provider`, t.technology_provider ?? "");

        const rawCost = t.cost ?? "";
        if (
          rawCost !== null &&
          rawCost !== undefined &&
          String(rawCost).trim() !== ""
        ) {
          const parsed = Number(String(rawCost).replace(/,/g, "").trim());
          fd.append(
            `technology_${idx}_cost`,
            Number.isFinite(parsed) ? String(parsed) : String(rawCost)
          );
        } else {
          fd.append(`technology_${idx}_cost`, "");
        }

        fd.append(`technology_${idx}_areas_of_impact`, t.areas_of_impact ?? "");
        fd.append(`technology_${idx}_remarks`, t.remarks ?? "");
      }
    } catch (err) {
      console.warn("Error appending indexed technology fields:", err);
    }

    // then include techArr into newFields you merge into state:
    // newFields.technologies = techArr;
    // 8) POST to endpoint (use your existing apiClient)
    const endpoint = "/registrations/"; // keep your route

    try {
      // Let axios set Content-Type & boundary automatically (don't set multipart header manually)
      await apiClient.post(endpoint, fd);

      // Optionally update formData with merged fields to keep UI consistent
      if (typeof setFormData === "function") {
        setFormData((prev) => ({
          ...prev,
          firstname: data.firstname,
          lastname: data.lastname,
          userid: data.userid,
          company_name: data.company_name,
          organisationname: data.organisationname,
        }));
      }
      // When User save the Data It will Prefill the form data and fill pkId
      prefillFormData();
    } catch (err) {
      console.error("Exploration submission error:", err?.response || err);

      // Build a friendly message from DRF response (typical structure: { field: [errors] } )
      const resp = err?.response?.data;
      let message = "Submission failed.";

      if (!resp) {
        message = err?.message || message;
      } else if (typeof resp === "string") {
        message = resp;
      } else if (typeof resp === "object") {
        // If organisation_name error exists (server-side "limit 1" message), show it prominently
        if (resp.organisation_name) {
          message = Array.isArray(resp.organisation_name)
            ? resp.organisation_name.join(" | ")
            : String(resp.organisation_name);
        } else {
          // flatten errors
          const parts = [];
          for (const [k, v] of Object.entries(resp)) {
            if (Array.isArray(v)) parts.push(`${k}: ${v.join(" | ")}`);
            else parts.push(`${k}: ${JSON.stringify(v)}`);
          }
          message = parts.length ? parts.join("\n") : JSON.stringify(resp);
        }
      }

      alert("Submission failed: " + message);
    }
  }

  // end handleSubmit

  // Rendering steps content
  const renderStepContent = () => {
    const progress = ((currentStep - 1) / 4) * 100;

    return (
      <div
        className="form-step"
        role="form"
        aria-label={`Step ${currentStep} of 5`}
      >
        <div className="progress-bar" aria-hidden="true">
          <div className="progress" style={{ width: `${progress}%` }} />
        </div>

        {currentStep === 1 && (
          <>
            <h3 className="step-title">Step 1: Organization Details</h3>

            <div className="form-group">
              <label htmlFor="organisationname">
                Organisation Name:{" "}
                {/* <span aria-hidden="true" className="text-red">
                  *
                </span> */}
              </label>
              <input
                id="organisation_name"
                name="organisationname"
                type="text"
                disabled={!!formData.organisationname}
                maxLength={FIELD_MAX_LENGTH}
                value={formData.organisationname}
                aria-describedby="organisation_name-error"
                className="form-input"
                placeholder="Enter organisation name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="mailing_address">
                Postal Address:{" "}
                <span aria-hidden="true" className="text-red">
                  *
                </span>
              </label>
              <textarea
                id="mailing_address"
                name="mailing_address"
                value={formData.mailing_address}
                rows={3}
                maxLength={FIELD_MAX_LENGTH}
                onChange={(e) =>
                  handleChange("mailing_address", e.target.value)
                }
                aria-describedby="mailing_address-error"
                placeholder="Enter postal address"
                className={`form-textarea ${
                  !formData.mailing_address.trim() && currentStep === 1
                    ? "has-error"
                    : ""
                }`}
                required
              />
              {fieldErrors.mailing_address && (
                <span
                  className="error-tooltip"
                  id="mailing_address-error"
                  role="alert"
                >
                  {fieldErrors.mailing_address}
                </span>
              )}
              {!formData.mailing_address.trim() && currentStep === 1 && (
                <span
                  className="error-tooltip"
                  id="mailing_address-error"
                  role="alert"
                >
                  Mailing address is required
                </span>
              )}
            </div>
          </>
        )}

        {currentStep === 2 && (
          <>
            <h3 className="step-title">
              Step 2: Approving Authority & Contact
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section
                aria-labelledby="approving-authority-heading"
                className="step-section"
              >
                <h4 id="approving-authority-heading">Approving Authority</h4>
                <p className="note">
                  Approving authority should be concerned Director /Board level
                  executive.{" "}
                </p>

                <div className="form-group">
                  <label htmlFor="authority_name">
                    Name:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="authority_name"
                    name="authority_name"
                    type="text"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authority_name}
                    onChange={(e) =>
                      handleChange("authority_name", e.target.value)
                    }
                    aria-describedby="authority_name-error"
                    placeholder="Name"
                    className={`form-input ${
                      !formData.authority_name && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="authority_title">
                    Designation:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="authority_title"
                    name="authority_title"
                    type="text"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authority_title}
                    onChange={(e) =>
                      handleChange("authority_title", e.target.value)
                    }
                    aria-describedby="authority_title-error"
                    placeholder="Designation"
                    className={`form-input ${
                      !formData.authority_title && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="authorityLandline">
                    Landline:{" "}
                    <span className="text-red" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityLandline"
                    type="number"
                    name="authorityLandline"
                    value={formData.authorityLandline}
                    onChange={(e) =>
                      handleChange("authorityLandline", e.target.value)
                    }
                    // onBlur={(e) => handleBlur('authorityLandline', e.target.value)}
                    className={`form-input ${
                      !formData.authorityLandline && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    placeholder="Landline number"
                    aria-required="true"
                    aria-describedby={
                      fieldErrors.authorityLandline
                        ? "authorityLandline-error"
                        : undefined
                    }
                  />
                  {/* {!formData.authorityLandline && currentStep === 2 && <span className="error-tooltip">Authority Landline Number is required</span>} */}
                </div>
                <div className="form-group">
                  <label htmlFor="authority_phone">
                    Mobile:
                    {/* <span className="text-red" aria-hidden="true"></span> */}
                  </label>
                  <input
                    id="authority_phone"
                    type="tel"
                    name="authority_phone"
                    value={formData.authority_phone}
                    onChange={(e) =>
                      handleChange("authority_phone", e.target.value)
                    }
                    // onBlur={(e) => handleBlur('authorityPhone', e.target.value)}
                    className={`form-input`}
                    placeholder="Phone number"
                    maxLength={PHONE_MAX_LENGTH}
                    aria-required="true"
                    // aria-describedby={fieldErrors.authorityPhone ? 'authorityPhone-error' : undefined}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="authority_email">
                    Email:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="authority_email"
                    name="authority_email"
                    type="email"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authority_email}
                    onChange={(e) =>
                      handleChange("authority_email", e.target.value)
                    }
                    aria-describedby="authority_email-error"
                    placeholder="Approving authority's email"
                    className={`form-input ${
                      !formData.authority_email && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                </div>
              </section>

              <section
                aria-labelledby="contacts-heading"
                className="step-section"
              >
                <h4 id="contacts-heading">
                  Contacts (Nodal Officials){" "}
                  <span aria-hidden="true" className="text-red">
                    *
                  </span>
                </h4>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="copyApplicantData"
                      // checked={copyApplicantData}
                      onChange={handleCopyApplicantToggle}
                      className="form-checkbox"
                    />{" "}
                    Same as applicant
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="contact_name">
                    Name{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="contact_name"
                    name="contact_name"
                    type="text"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.contact_name}
                    onChange={(e) =>
                      handleChange("contact_name", e.target.value)
                    }
                    placeholder="Contact name"
                    disabled={copyApplicantData}
                    className={`form-input ${
                      !formData.contact_name && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    aria-describedby="contact_name-error"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact_phone">
                    Phone:
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="contact_phone"
                    name="contact_phone"
                    type="tel"
                    maxLength={PHONE_MAX_LENGTH}
                    value={formData.contact_phone}
                    onChange={(e) =>
                      handleChange("contact_phone", e.target.value)
                    }
                    aria-describedby="contact_phone-error"
                    disabled={copyApplicantData}
                    placeholder="10-digit phone number"
                    className={`form-input ${
                      !formData.contact_phone && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact_email">Email</label>
                  <input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.contact_email}
                    onChange={(e) =>
                      handleChange("contact_email", e.target.value)
                    }
                    placeholder="Contact email"
                    disabled={copyApplicantData}
                    className={`form-input ${
                      fieldErrors.contact_email ? "has-error" : ""
                    }`}
                    aria-describedby="contact_email-error"
                  />
                </div>
              </section>
            </div>

            <div className="form-group">
              <label htmlFor="company_profile">
                Brief write up on company profile and the activities with
                specific reference to Exploration operations during the year
                2024-25.
              </label>
              <p className="note">(within 300 words) </p>
              <textarea
                id="company_profile"
                name="company_profile"
                value={formData.company_profile}
                rows={6}
                // maxLength={COMPANY_PROFILE_MAX_LENGTH}
                onChange={(e) =>
                  handleChange("company_profile", e.target.value)
                }
                className="form-textarea"
                placeholder="Enter company profile"
              />
            </div>
          </>
        )}

        {/* Step 3: Quantitative Information - Part 1 */}
        {currentStep === 3 && (
          <>
            <h3 className="step-title">
              Step 3: Quantitative Information - Part 1
            </h3>
            <table className="quant-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Particulars</th>
                  <th>2024-25</th>
                  <th>2023-24</th>
                </tr>
              </thead>
              <tbody>
                {explorationFields.slice(0, 5).map(({ num, label, key }) => (
                  <tr key={key || num}>
                    <td>{num}</td>
                    <td>{label}</td>
                    {/* Input for 2024 */}
                    <td>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        inputProps={{
                          inputMode: "decimal", // brings up numeric keypad on mobile
                          pattern: "[0-9]\\.?[0-9]", // allows digits + decimal
                        }}
                        name={key}
                        value={
                          Array.isArray(formData[key])
                            ? formData[key][0] || ""
                            : ""
                        }
                        onChange={(e) => {
                          let val = e.target.value;

                          // Allow only digits and one decimal point
                          val = val.replace(/[^0-9.]/g, ""); // remove non-numeric except "."
                          const parts = val.split(".");
                          if (parts.length > 2) {
                            // keep only first decimal point
                            val = parts[0] + "." + parts.slice(1).join("");
                          }

                          handleChange(e.target.name, val, 0);
                        }}
                        onWheel={(e) => e.target.blur()} // prevent mouse scroll increment
                        onKeyDown={(e) => {
                          // Prevent arrow up/down, exponential 'e', and plus/minus signs
                          if (
                            e.key === "ArrowUp" ||
                            e.key === "ArrowDown" ||
                            e.key === "e" ||
                            e.key === "E" ||
                            e.key === "+" ||
                            e.key === "-"
                          ) {
                            e.preventDefault();
                          }
                        }}
                      />
                      {fieldErrors[`${key}[0]`] && (
                        <span className="error-tooltip" role="alert">
                          {fieldErrors[`${key}[0]`]}
                        </span>
                      )}
                    </td>
                    {/* Input for 2023 */}
                    <td>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        inputProps={{
                          inputMode: "decimal", // brings up numeric keypad on mobile
                          pattern: "[0-9]\\.?[0-9]", // allows digits + decimal
                        }}
                        name={key}
                        value={
                          Array.isArray(formData[key])
                            ? formData[key][1] || ""
                            : ""
                        }
                        onChange={(e) => {
                          let val = e.target.value;

                          // Allow only digits and one decimal point
                          val = val.replace(/[^0-9.]/g, ""); // remove non-numeric except "."
                          const parts = val.split(".");
                          if (parts.length > 2) {
                            // keep only first decimal point
                            val = parts[0] + "." + parts.slice(1).join("");
                          }

                          handleChange(e.target.name, val, 1);
                        }}
                        onWheel={(e) => e.target.blur()} // prevent mouse scroll increment
                        onKeyDown={(e) => {
                          // Prevent arrow up/down, exponential 'e', and plus/minus signs
                          if (
                            e.key === "ArrowUp" ||
                            e.key === "ArrowDown" ||
                            e.key === "e" ||
                            e.key === "E" ||
                            e.key === "+" ||
                            e.key === "-"
                          ) {
                            e.preventDefault();
                          }
                        }}
                      />
                      {fieldErrors[`${key}[1]`] && (
                        <span className="error-tooltip" role="alert">
                          {fieldErrors[`${key}[1]`]}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {currentStep === 4 && (
          <>
            <h3 className="step-title">
              Step 4: Quantitative Information - Part 2
            </h3>
            <table className="quant-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Particulars</th>
                  <th>2024-25</th>
                  <th>2023-24</th>
                </tr>
              </thead>
              <tbody>
                {explorationFields.slice(5).map(({ num, label, key }) => {
                  if (!key) {
                    // Non-input row
                    return (
                      <tr key={num}>
                        <td>{num}</td>
                        <td>{label}</td>
                        <td colSpan={2} className="non-editable-cell">
                          —
                        </td>
                      </tr>
                    );
                  }

                  // Check if main 9 is fully filled
                  const isMain9Filled =
                    Array.isArray(formData["energy_exploration"]) &&
                    formData["energy_exploration"].every(
                      (v) => v !== "" && v !== null && v !== undefined
                    );

                  // Check if any of the subitems 9.1–9.5 have values
                  const isAnySubFilled = [
                    "9.1",
                    "9.2",
                    "9.3",
                    "9.4",
                    "9.5",
                  ].some((subNum) => {
                    const subKey = explorationFields.find(
                      (f) => f.num === subNum
                    )?.key;
                    return (
                      subKey &&
                      Array.isArray(formData[subKey]) &&
                      formData[subKey].some(
                        (v) => v !== "" && v !== null && v !== undefined
                      )
                    );
                  });

                  // Disable subfields if main 9 is filled
                  const disableSubFields =
                    isMain9Filled &&
                    ["9.1", "9.2", "9.3", "9.4", "9.5"].includes(num);

                  // Disable main 9 if any subitem is filled
                  const disableMain9 = num === "9" && isAnySubFilled;

                  return (
                    <tr key={key}>
                      <td>{num}</td>
                      <td>{label}</td>
                      {[0, 1].map((yearIndex) => (
                        <td key={yearIndex}>
                          <TextField
                            type="text" // keep text to avoid browser spinners
                            variant="outlined"
                            size="small"
                            fullWidth
                            name={key}
                            value={
                              Array.isArray(formData[key])
                                ? formData[key][yearIndex] ?? ""
                                : ""
                            }
                            onChange={(e) => {
                              let val = e.target.value;

                              // Allow only digits and one decimal point
                              val = val.replace(/[^0-9.]/g, "");
                              const parts = val.split(".");
                              if (parts.length > 2) {
                                // keep only the first decimal point
                                val = parts[0] + "." + parts.slice(1).join("");
                              }

                              handleChange(e.target.name, val, yearIndex);
                            }}
                            onKeyDown={(e) => {
                              // block arrows, minus, exponential, plus
                              if (
                                ["-", "+", "e", "E"].includes(e.key) ||
                                e.key === "ArrowUp" ||
                                e.key === "ArrowDown"
                              ) {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => e.target.blur()} // disable mouse scroll
                            className="form-input no-spinner"
                            disabled={disableSubFields || disableMain9}
                            sx={{
                              "& .MuiInputBase-root.Mui-disabled": {
                                backgroundColor: "#f5f5f5",
                                color: "rgba(0,0,0,0.6)",
                              },
                            }}
                            inputProps={{
                              inputMode: "decimal", // mobile keypad for decimal/numeric
                              pattern: "[0-9]\\.?[0-9]", // digits + optional decimal
                            }}
                          />
                          {fieldErrors[`${key}[${yearIndex}]`] && (
                            <span className="error-tooltip" role="alert">
                              {fieldErrors[`${key}[${yearIndex}]`]}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <table className="quant-table">
              <thead>
                <tr>
                  <th colSpan="1">12</th>
                  <th colSpan="7">
                    <p className="tablerowq">New Technologies Adopted</p>
                  </th>
                </tr>
              </thead>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Technology Name</th>
                  <th>Technology Provider</th>
                  <th>Cost of the Technology (INR Crore) </th>
                  <th>Areas of Impact </th>
                  <th>Remarks(Max 50 words)</th>
                </tr>
              </thead>
              <tbody>
                {formData.technologies &&
                  formData.technologies.map((tech, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>
                        <input
                          type="text"
                          name={`technologies.${i}.technology_name`}
                          maxLength={FIELD_MAX_LENGTH}
                          value={tech.technology_name || ""}
                          onChange={(e) =>
                            handleTechChange(
                              i,
                              "technology_name",
                              e.target.value
                            )
                          }
                          className="form-input"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          name={`technologies.${i}.technology_provider`}
                          maxLength={FIELD_MAX_LENGTH}
                          value={tech.technology_provider || ""}
                          onChange={(e) =>
                            handleTechChange(
                              i,
                              "technology_provider",
                              e.target.value
                            )
                          }
                          className="form-input"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          name={`technologies.${i}.cost`}
                          value={tech.cost || ""}
                          onChange={(e) =>
                            handleTechChange(i, "cost", e.target.value)
                          }
                          className="form-input"
                          onKeyDown={(e) => {
                            if (["-", "e", "E"].includes(e.key))
                              e.preventDefault();
                          }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          name={`technologies.${i}.areas_of_impact`}
                          maxLength={FIELD_MAX_LENGTH}
                          value={tech.areas_of_impact || ""}
                          onChange={(e) =>
                            handleTechChange(
                              i,
                              "areas_of_impact",
                              e.target.value
                            )
                          }
                          className="form-input"
                        />
                      </td>
                      <td>
                        <textarea
                          rows={2}
                          maxLength={50}
                          name={`technologies.${i}.remarks`}
                          value={tech.remarks || ""}
                          onChange={(e) =>
                            handleTechChange(i, "remarks", e.target.value)
                          }
                          className="form-textarea"
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            {/* Comments Section */}
            <div className="form-group" style={{ marginTop: "1rem" }}>
              <label htmlFor="comment">Comments</label>
              <textarea
                id="comment"
                name="comment"
                // maxLength={COMMENT_MAX_LENGTH}
                value={formData.comment || ""}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                placeholder="Comments in (200 words) against input parameter, if any"
                className="form-textarea"
                rows={4}
              />
              {fieldErrors.comment && (
                <span className="error-tooltip" role="alert">
                  {fieldErrors.comment}
                </span>
              )}
            </div>
          </>
        )}

        {currentStep === 5 && (
          <>
            <h3 className="step-title">Step 5: Attachments & Declaration</h3>

            <div className="form-group">
              <label>List of Attachments (Optional):</label>
              <table className="quant-table">
                <thead>
                  <tr>
                    <th>S. No.</th>
                    <th>Description</th>
                    <th>Upload (jpg, png, pdf; max 5 MB)</th>
                  </tr>
                </thead>
                {/* <tbody>
                  {[1, 2, 3, 4].map((num) => {
                    const key = `attachments${num}`;
                    const attachment = formData[key];
                    return (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>
                          <input
                            type="text"
                            name={`${key}.description`}
                            value={attachment.description}
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "description",
                                e.target.value
                              )
                            }
                            placeholder="Enter description"
                            maxLength={FIELD_MAX_LENGTH}
                            className="form-input"
                          />
                        </td>
                        <td>
                          <input
                            type="file"
                            accept=".jpg,.png,.pdf"
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "file",
                                e.target.files[0],
                                e
                              )
                            }
                            className="form-input mt-4"
                          />
                          {attachment.file && (
                            <p className="file-name">
                              Selected file: {attachment.file.name}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody> */}
                <tbody>
                  {[1, 2, 3, 4].map((num) => {
                    const key = `attachments${num}`;
                    const attachment = formData[key] || { description: "", file: null };
                    return (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>
                          <input type="text" name={`${key}.description`} value={attachment.description} onChange={(e) => handleAttachmentChange(key, "description", e.target.value)} placeholder="Enter description" maxLength={FIELD_MAX_LENGTH} className="form-input" />
                        </td>
                        <td>
                          <input type="file" accept=".jpg,.png,.pdf" onChange={(e) => handleAttachmentChange(key, "file", e.target.files[0], e)} className="form-input mt-4" />
                          {attachment.file && <p className="file-name">Selected file: {attachment.file.name}</p>}
                          {attachment.url && !attachment.file && (
  <p className="file-name">
    Uploaded file:{" "}
    <button
      type="button"
      onClick={() => viewFile(attachment.url)}
      style={{ background: "none", border: "none", padding: 0, color: "#06c", cursor: "pointer", textDecoration: "underline" }}
    >
      View
    </button>
  </p>
)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="form-group">
              <label>
                Print the completed application form and upload it with the
                signature of the approving authority. Without signature of the
                approving authority, application will not be considered valid.
                <span aria-hidden="true" className="text-red">
                  *
                </span>
              </label>
              <div className="form-navigation">
                <button
                  type="button"
                  onClick={handlePrint}
                  className="btn btn-outline"
                >
                  Print Preview
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="approving_authority_file">
                Upload Document with Approving Authority Signature
                (Director/Board Level)
                <span aria-hidden="true" className="text-red">
                  *
                </span>
                :
              </label>
              <input
                type="file"
                id="approving_authority_file"
                accept=".jpg,.png,.pdf"
                onChange={(e) => {
                  const files = e.target.files;
                  if (files.length > 0) {
                    const confirmed = window.confirm(
                      "Are you sure the form is approved and signed by approving authority?"
                    );
                    if (!confirmed) {
                      // If user clicks "Cancel", clear the input
                      e.target.value = "";
                      return;
                    }
                    handleApprovingAuthorityChange(files);
                  }
                }}
                className={`form-input mt-4 ${
                  fieldErrors.approving_authority_file ? "has-error" : ""
                }`}
                aria-describedby="approving_authority_file-error"
                required
              />
              {formData.approving_authority_file && (
                <p className="file-name">
                  Selected file: {formData.approving_authority_file.name}
                </p>
              )}
               {formData.approvingAuthoritySignatureUrl && !formData.approvingAuthoritySignature && (
    <p className="file-name">
      Uploaded file:{" "}
      <button
        type="button"
        onClick={() => viewFile(formData.approvingAuthoritySignatureUrl)}
        style={{ background: "none", border: "none", padding: 0, color: "#06c", cursor: "pointer", textDecoration: "underline" }}
      >
        View
      </button>
    </p>
  )}
              {fieldErrors.approving_authority_file && (
                <span
                  className="error-tooltip"
                  id="approving_authority_file-error"
                  role="alert"
                >
                  {fieldErrors.approving_authority_file}
                </span>
              )}
            </div>

            <div className="form-group">
              <label>
                <input
                  id="declaration"
                  type="checkbox"
                  name="declaration"
                  checked={formData.declaration}
                  onChange={(e) =>
                    handleChange("declaration", e.target.checked)
                  }
                  className={`form-checkbox ${
                    fieldErrors.declaration ? "has-error" : ""
                  }`}
                  aria-describedby="declaration-error"
                  required
                />{" "}
                I declare that the information submitted is true and complete.
              </label>
              {fieldErrors.declaration && (
                <span
                  className="error-tooltip"
                  id="declaration-error"
                  role="alert"
                >
                  {fieldErrors.declaration}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  useEffect(() => {
    const middlePosition = window.innerHeight / 2; // middle
    window.scrollTo({ top: middlePosition + 100, behavior: "smooth" });
  }, [currentStep]);

  return (
    <div className="sidebar-guideline-container">
      <SidebarGuideline
        isOpen={true}
        sidebarItems={[{ id: "Guideline", label: "Guideline" }]}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        selectedAwardCategory={formData.category}
      />
      <div className="application-form">
        <div className="form-header">
          <h1>{awardTitle}</h1>
          <h6>Step {currentStep} of 5</h6>
        </div>
        {error && <div className="error">{error}</div>}
        {isSubmitted ? (
          <div className="thank-you-message">
            <h2>Thank you for your submission!</h2>
            <p>Your Application has been successfully submitted.</p>
            <button onClick={() => navigate("/#Award%20Categories")}>
              Submit Another Application
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {renderStepContent()}
            {currentStep === 1 && (
              <div className="form-navigation-step1">
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="btn btn-outline"
                  disabled={isSubmited()}
                >
                  <Save size={16} /> Save Draft
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn btn-primary"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}
            {currentStep > 1 && (
              <div className="form-navigation">
                <button
                  type="button"
                  onClick={prevStep}
                  className="btn btn-outline"
                >
                  <ChevronLeft size={16} /> Previous
                </button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="btn btn-outline"
                  disabled={isSubmited()}
                >
                  <Save size={16} /> Save Draft
                </button>
                {currentStep < 5 && (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn btn-primary"
                  >
                    Next <ChevronRight size={16} />
                  </button>
                )}
                {currentStep === 5 && (
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={isSubmited()}
                    onClick={(e) => {
                      if (!formData.declaration) {
                        return alert(
                          "Please accept the declaration before submitting."
                        );
                      }
                      if (!formData.approving_authority_file) {
                        return alert(
                          "Please upload the approving authority signature file."
                        );
                      }
                      if (!window.confirm("Are you sure you want to submit?")) {
                        e.preventDefault();
                      }
                      handleSubmit(null, "submit");
                      alert("Exploration registration submitted successfully!");
                      setIsSubmitted(true);

                      handleSubmit(e, "submit");
                    }}
                  >
                    Submit
                  </button>
                )}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default RegistrationExploration;
